import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import { FormattedMessage, useIntl } from 'react-intl'
import Radio from 'components/shared/Radio/index'
import { useResolutions } from 'store/resolutions'
import { VOUCHER_DELIVERY_PRICE } from 'constants/prices'
import { formatPrice } from 'helpers/helpers'
import { useAppSelector } from 'store/hooks'
import { useDispatch } from 'react-redux'
import { setErrors, setDeliveryType } from 'store/order-slice'
import {
    useCreateCartItemMutation,
    useDeleteCartItemByIdMutation,
    useGetProductQuery,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { v4 as uuidv4 } from 'uuid'
import { useCart } from 'store/cart-slice/hooks'
import {
    estimatedDeliveryDate,
    isCurrentDateTimeWithinRange,
} from 'helpers/dates'
import { handleError } from 'helpers/errors'
import Icon from 'components/shared/Icon'

const CART_CREATOR_TYPE = 'voucher'

const DeliveryOptions = () => {
    const dispatch = useDispatch()
    const { language } = useApp()
    const { formatMessage } = useIntl()
    const { isTablet, isMobile } = useResolutions()
    const { refetchCart, isCartFetching } = useCart()

    const { deliveryType } = useAppSelector((state) => state.order.shipping)
    const shippingCartId = useAppSelector(
        (state) => state.cart.items.shipping?.cartItemId
    )

    const [postCartItem, postStatus] = useCreateCartItemMutation()
    const [deleteCartItem, deleteStatus] = useDeleteCartItemByIdMutation()

    const { data } = useGetProductQuery({
        _locale: language,
        productType: 'shipping',
    })

    const shippingProductId = data?.productId
    const isProcessing =
        postStatus.isLoading || deleteStatus.isLoading || isCartFetching

    const setDeliveryOption = async (value: string) => {
        if (value !== 'email' && value !== 'shipping') return

        dispatch(setDeliveryType(value))
        dispatch(setErrors([]))

        if (!shippingCartId && shippingProductId && value === 'shipping') {
            try {
                await postCartItem({
                    _locale: language,
                    cartCreatorType: CART_CREATOR_TYPE,
                    createCartItemCommand: {
                        cartItemId: uuidv4(),
                        productId: shippingProductId,
                        productType: 'shipping',
                        quantity: 1,
                    },
                })
                refetchCart()
            } catch (error) {
                handleError(error)
            }
        } else if (shippingCartId && value === 'email') {
            try {
                await deleteCartItem({
                    _locale: language,
                    cartItemId: shippingCartId,
                    cartCreatorType: CART_CREATOR_TYPE,
                })

                refetchCart()
            } catch (error) {
                handleError(error)
            }
        }
    }

    return (
        <Box mb={40} gap={[16, 40]} direction={['column', 'row']} isFullWidth>
            <Radio
                options={[
                    {
                        option: formatMessage({
                            id: 'selfcare.payment.voucher_email_delivery_option',
                            defaultMessage: 'E-mail – za darmo!',
                        }),
                        id: 'email',
                        tooltipMessage: formatMessage({
                            id: 'vouchers.payment.email_delivery_tooltip',
                            defaultMessage:
                                'Podaj adres e-mail, na który zostanie wysłany voucher. Może to być adres osoby, którą chcesz obdarować lub Twój własny. ',
                        }),
                    },
                    {
                        option: `${formatMessage(
                            {
                                id: 'selfcare.payment.voucher_post_delivery_option',
                                defaultMessage: 'Wysyłka kurierem +{price} PLN',
                            },
                            {
                                price: formatPrice(VOUCHER_DELIVERY_PRICE),
                            }
                        )}`,
                        id: 'shipping',
                        tooltipMessage: formatMessage({
                            id: 'vouchers.payment.post_delivery_tooltip',
                            defaultMessage:
                                'Podaj adres dostawy, na który wyślemy Twój voucher.',
                        }),
                        additionalDescription: isCurrentDateTimeWithinRange(
                            '2024-12-16 00:00',
                            '2024-12-19 15:00'
                        ) ? (
                            <Box direction="row" gap={4}>
                                <Icon variant="gift" />
                                <Text variant="body16" color="success" mt={2}>
                                    <FormattedMessage
                                        id="annual.payment.delivery_by_courier_christmas_description"
                                        defaultMessage="Zamów do 15:00 dn. 19.12 z gwarancją dostawy do świąt!"
                                    />
                                </Text>
                            </Box>
                        ) : (
                            <Text variant="body">
                                <FormattedMessage
                                    id="annual.payment.delivery_by_courier_option_description"
                                    defaultMessage="{estimate_delivery_date} u Ciebie"
                                    values={{
                                        estimate_delivery_date:
                                            estimatedDeliveryDate,
                                    }}
                                />
                            </Text>
                        ),
                    },
                ]}
                direction={isTablet ? 'column' : 'row'}
                value={deliveryType}
                setValue={setDeliveryOption}
                isDisabled={isProcessing}
                isTooltipTop={!isMobile}
                isTooltipPrimary
                testIdPrefix="payment-delivery"
                isFullWidth
            />
        </Box>
    )
}

export default DeliveryOptions
